import React from 'react';
import { motion } from 'framer-motion';
import {
    Globe, Layout, ShoppingCart, Zap, Shield, Search,
    Smartphone, Code2, Server, Globe2, Chrome,
    Gauge, PenTool, Database, Layers, ArrowRight, ChevronLeft
} from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO_Component from '../../components/SEO';

const WebsiteDevelopment = () => {
    const capabilities = [
        { title: "React & Next.js", desc: "High-performance, SEO-friendly web applications.", icon: Code2 },
        { title: "E-commerce", desc: "Scalable online stores with complex integrations.", icon: ShoppingCart },
        { title: "Dashboard UI", desc: "Clean admin interfaces and data visualization.", icon: Layout },
        { title: "API Backend", desc: "Secure and lightning-fast server architectures.", icon: Server },
        { title: "Web Vitals", desc: "Optimizing for Core Web Vitals and LCP.", icon: Gauge },
        { title: "Cyber Security", desc: "Deep security audits and data protection.", icon: Shield }
    ];

    return (
        <div className="bg-[#02000c] min-h-screen text-white pt-20 overflow-hidden">
            <SEO_Component
                title="Website Development | Modern React & Next.js Solutions - MehareTech"
                description="We engineer high-performance, SEO-friendly websites using React, Next.js, and Node.js. Specialized in scalable e-commerce, enterprise dashboards, and custom web applications."
                keywords="website development company, React JS developers, Next js web agency India, custom web application, ecommerce website builder, responsive web design, enterprise software development"
            />

            {/* Back to Services Link */}
            <div className="container mx-auto px-6 mb-4">
                <Link to="/services" className="inline-flex items-center gap-2 text-blue-500 font-bold uppercase tracking-widest text-[10px] hover:text-white transition-colors group">
                    <ChevronLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
                    Back to Services
                </Link>
            </div>

            {/* Hero Section */}
            <section className="relative py-12 px-6">
                <div className="absolute top-0 right-0 w-1/3 h-full bg-blue-600/10 blur-[120px] -z-10"></div>

                <div className="container mx-auto text-center max-w-4xl">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.6 }}
                    >
                        <span className="text-blue-500 font-black uppercase tracking-[0.3em] text-[10px] mb-2 block">
                            Web Architecture
                        </span>
                        <h1 className="text-4xl md:text-6xl font-black mb-6 leading-tight tracking-tighter">
                            MODERN <span className="bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 bg-clip-text text-transparent">WEB PERFORMANCE</span>
                        </h1>
                        <p className="text-lg text-slate-400 mb-8 max-w-2xl mx-auto font-medium">
                            We build fast, secure, and visually stunning web experiences.
                        </p>
                        <div className="flex justify-center gap-4">
                            <div className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full text-[10px] font-bold uppercase tracking-widest text-blue-400">
                                <Gauge size={14} /> Performance: 99+
                            </div>
                            <div className="flex items-center gap-2 px-4 py-2 bg-green-500/10 border border-green-500/20 rounded-full text-[10px] font-bold uppercase tracking-widest text-green-400">
                                <Shield size={14} /> Secure
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Grid */}
            <section className="py-12 px-6 bg-slate-900/40 border-y border-white/5">
                <div className="container mx-auto">
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                        {capabilities.map((cap, i) => (
                            <motion.div
                                key={i}
                                className="p-6 bg-slate-900 border border-white/5 rounded-2xl hover:border-blue-500/30 transition-all group"
                            >
                                <cap.icon size={26} className="text-blue-500 mb-4 group-hover:scale-110 transition-transform" />
                                <h3 className="text-lg font-bold mb-1">{cap.title}</h3>
                                <p className="text-slate-400 text-[11px] leading-relaxed">{cap.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Tech Section */}
            <section className="py-12 px-6">
                <div className="container mx-auto">
                    <div className="flex flex-col md:flex-row items-center gap-8">
                        <div className="md:w-1/2">
                            <h2 className="text-3xl font-black mb-6 tracking-tight">
                                Scalable <span className="text-blue-500">Solutions</span>
                            </h2>
                            <p className="text-slate-400 text-sm mb-6 leading-relaxed">
                                From simple landing pages to complex enterprise platforms, we ensure your web presence is optimized for search engines and user engagement.
                            </p>
                            <button className="px-6 py-3 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-all text-xs">
                                Discuss Your Project
                            </button>
                        </div>
                        <div className="md:w-1/2 relative">
                            <img
                                src="https://images.pexels.com/photos/1779487/pexels-photo-1779487.jpeg"
                                alt="Web Dev"
                                className="rounded-3xl shadow-xl w-full h-48 object-cover border border-white/10"
                            />
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default WebsiteDevelopment;
